import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { fetchReferral, fetchGamify, fetchLeaderboard } from "@/lib/api";
import { PageHero } from "@/components/PageHero";
import ShareButton from "@/components/ShareButton";
import { HERO_ART } from "@/lib/heroes";
import { toast } from "sonner";
import { Copy, Check, Gift, Users, Sparkles, Trophy, Flame, ArrowRight } from "lucide-react";

export default function Refer() {
  const { pick } = useLang();
  const { user, ready } = useAuth();
  const [ref, setRef] = useState(null);
  const [profile, setProfile] = useState(null);
  const [board, setBoard] = useState([]);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!ready || !user) return;
    fetchReferral().then(setRef).catch(() => {});
    fetchGamify().then(setProfile).catch(() => {});
    fetchLeaderboard().then(setBoard).catch(() => setBoard([]));
  }, [ready, user]);

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const inviteLink = ref ? `${origin}/register?ref=${ref.code}` : "";
  const perRef = ref?.points_per_referral || 50;
  const earned = ref ? ref.referrals * perRef : 0;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
      setCopied(true);
      toast(pick("आमंत्रण लिंक कॉपी हो गया", "Invite link copied"));
      setTimeout(() => setCopied(false), 1800);
    } catch { /* ignore */ }
  };

  return (
    <div data-testid="refer-page">
      <Helmet><title>Invite &amp; Earn · Dharmartha</title>
        <meta name="description" content="Invite fellow seekers to Dharmartha and earn sadhana points for every friend who joins. Grow the community and climb the leaderboard." /></Helmet>

      <PageHero
        art={HERO_ART.devotion}
        overline={pick("आमंत्रण · अंक अर्जित करें", "Invite · Earn points")}
        deva="आमंत्रण"
        en={pick("आमंत्रित करें एवं अंक पाएँ", "Invite friends & earn")}
        tagline={pick(`प्रत्येक साथी के जुड़ने पर आप ${perRef} अंक अर्जित करते हैं — और वे भी बोनस पाते हैं। सामूहिक साधना को आगे बढ़ाएँ।`, `Earn ${perRef} points for every friend who joins — they get a welcome bonus too. Grow the community of sadhaks.`)}
      />

      <section className="mx-auto max-w-4xl px-5 py-14 md:px-8 md:py-16">
        {ready && !user ? (
          <div className="border border-line bg-[var(--surface-alt)]/40 p-6 text-center" data-testid="refer-login-prompt">
            <p className="font-serif-latin text-lg text-ink-2">
              {pick("अपना आमंत्रण लिंक पाने हेतु ", "To get your invite link, ")}
              <Link to="/register" className="text-saffron underline">{pick("निःशुल्क खाता बनाएँ", "create a free account")}</Link>
              {pick(" या ", " or ")}
              <Link to="/login" className="text-saffron underline">{pick("साइन इन करें", "sign in")}</Link>.
            </p>
          </div>
        ) : (
          <>
            {/* Stat strip */}
            <div className="grid grid-cols-3 gap-3 md:gap-4" data-testid="refer-stats">
              <StatBox icon={Users} value={ref ? ref.referrals : "…"} label={pick("आमंत्रित मित्र", "friends joined")} testid="refer-count" />
              <StatBox icon={Sparkles} value={ref ? earned : "…"} label={pick("अर्जित अंक", "points earned")} testid="refer-earned" />
              <StatBox icon={Gift} value={`+${perRef}`} label={pick("प्रति आमंत्रण", "per invite")} testid="refer-per" />
            </div>

            {/* Invite link card */}
            <div className="mt-8 border border-line bg-surface p-6 md:p-8" data-testid="refer-link-card">
              <p className="overline-label text-gold">{pick("आपका आमंत्रण लिंक", "Your invite link")}</p>
              <h2 className="mt-2 font-serif-latin text-2xl text-ink">{pick("इस लिंक को साझा करें", "Share this link")}</h2>
              <p className="mt-2 text-sm leading-relaxed text-ink-2">
                {pick("जब कोई इस लिंक से खाता बनाता है, तो आप दोनों को अंक मिलते हैं।", "When someone signs up with your link, you both earn points.")}
              </p>

              <div className="mt-5 flex flex-col gap-3 sm:flex-row">
                <div className="flex flex-1 items-center gap-2 overflow-hidden border border-line bg-parchment px-4 py-3">
                  <span className="truncate font-mono text-sm text-ink" data-testid="refer-link-value">{inviteLink || "…"}</span>
                </div>
                <button
                  onClick={copyLink}
                  data-testid="refer-copy-button"
                  disabled={!inviteLink}
                  className="inline-flex shrink-0 items-center justify-center gap-2 bg-[var(--saffron)] px-6 py-3 font-medium text-white transition-colors hover:bg-[var(--saffron-dark)] disabled:opacity-60"
                >
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  {copied ? pick("कॉपी हो गया", "Copied") : pick("लिंक कॉपी करें", "Copy link")}
                </button>
                <div className="shrink-0">
                  <ShareButton
                    testid="refer-share-button"
                    title="Dharmartha"
                    text={pick("धर्मार्थ पर सनातन धर्म का ज्ञान खोजें — मेरे साथ जुड़ें", "Discover the knowledge of Sanatana Dharma on Dharmartha — join me")}
                    url={inviteLink}
                    label={pick("साझा करें", "Share")}
                  />
                </div>
              </div>

              {ref?.code && (
                <p className="mt-4 text-xs text-ink-3">
                  {pick("आमंत्रण कोड", "Invite code")}: <span className="font-mono text-ink-2" data-testid="refer-code">{ref.code}</span>
                </p>
              )}
            </div>

            {/* How it works */}
            <div className="mt-8 grid gap-4 sm:grid-cols-3" data-testid="refer-how">
              <HowStep n="1" title={pick("लिंक साझा करें", "Share your link")} desc={pick("मित्रों एवं परिवार को अपना आमंत्रण लिंक भेजें।", "Send your invite link to friends and family.")} />
              <HowStep n="2" title={pick("वे जुड़ते हैं", "They join")} desc={pick("वे आपके लिंक से निःशुल्क खाता बनाते हैं।", "They create a free account through your link.")} />
              <HowStep n="3" title={pick("दोनों अंक पाएँ", "You both earn")} desc={pick(`आपको ${perRef} अंक एवं उन्हें स्वागत बोनस मिलता है।`, `You get ${perRef} points and they get a welcome bonus.`)} />
            </div>

            {/* Leaderboard tie-in */}
            <div className="mt-12" data-testid="refer-leaderboard">
              <div className="mb-5 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <Trophy className="h-5 w-5 text-gold" />
                  <h2 className="font-serif-latin text-2xl text-ink">{pick("सर्वाधिक अंक", "Top sadhaks")}</h2>
                </div>
                <Link to="/leaderboard" className="link-underline inline-flex items-center gap-1.5 text-sm text-saffron">
                  {pick("पूरी सूची", "Full leaderboard")} <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
              {profile && (
                <div className="mb-4 flex items-center justify-between border border-[var(--gold-muted)] bg-[rgba(176,141,87,0.06)] px-4 py-3" data-testid="refer-my-rank">
                  <span className="text-sm text-ink-2">
                    {pick("आपके अंक", "Your points")} · <span className="font-deva text-ink">{profile.level?.deva}</span>
                  </span>
                  <span className="font-serif-latin text-lg text-saffron">{profile.points?.toLocaleString()}</span>
                </div>
              )}
              {board.length > 0 && (
                <div className="overflow-hidden border border-line bg-surface">
                  <table className="w-full text-left text-sm">
                    <tbody>
                      {board.slice(0, 5).map((r, i) => (
                        <tr key={i} className="border-b border-line last:border-0">
                          <td className="px-4 py-3">
                            <span className={`inline-flex h-7 w-7 items-center justify-center rounded-full font-serif-latin ${i < 3 ? "bg-[rgba(199,80,0,0.08)] text-saffron" : "text-ink-3"}`}>{i + 1}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="font-deva-body text-ink">{r.name}</span>
                            {r.streak > 0 && <span className="ml-2 inline-flex items-center gap-0.5 text-xs text-saffron"><Flame className="h-3 w-3" />{r.streak}</span>}
                          </td>
                          <td className="px-4 py-3 text-right font-serif-latin text-lg text-ink">{r.points.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </section>
    </div>
  );
}

function StatBox({ icon: Icon, value, label, testid }) {
  return (
    <div data-testid={testid} className="flex flex-col items-center border border-line bg-surface px-3 py-6 text-center">
      <Icon className="mb-2 h-5 w-5 text-saffron" />
      <p className="font-serif-latin text-3xl text-ink">{value}</p>
      <p className="mt-1 text-xs text-ink-3">{label}</p>
    </div>
  );
}

function HowStep({ n, title, desc }) {
  return (
    <div className="border border-line bg-surface p-5">
      <span className="flex h-9 w-9 items-center justify-center rounded-full border border-[var(--gold)] font-serif-latin text-gold">{n}</span>
      <h3 className="mt-3 font-serif-latin text-lg text-ink">{title}</h3>
      <p className="mt-1.5 text-sm leading-relaxed text-ink-2">{desc}</p>
    </div>
  );
}
